import React from 'react';
import { useSelector } from 'react-redux';
import { Breadcrumb, Popconfirm } from 'antd';
import { useAtom, useAtomValue } from 'jotai';
import {
  Divider,
  HeaderWrapper,
  LeftSideHeader,
  RightSideHeader,
  StyledButton,
  Title,
} from './styled';
import { HeaderProps } from '../types';
import { useResetPanelAtoms, useUnsubscribeEmail } from '../hooks';
import {
  createSubscriptionAtom,
  editSubscriptionAtom,
  emailsToUnsubscribeAtom,
  isEditCreateFormOpenAtom,
} from '../atoms';
import {
  CloseOutlined,
  DeleteOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { isAdminRoleSelector } from '@src/store/common/selectors';
import { CURRENT_PRODUCT_MODE } from '@src/utils/constants';
import { DataSourceMapping } from '@src/pages/SavedDashboards/utils';
import AdminOnly from '@src/components/common/AdminOnly';
import apptracker from '@src/utils/sensor/apptracker';
import { UNSUBSCRIBE_EMAIL_SUBSCRIPTIONS } from '@src/utils/sensor/track';
import HelpRedirect from '@src/components/common/HelpRedirect';
import { AI_ALERT_EMAIL_SUBSCRIPTION_HELP } from '@src/utils/help/links';

export const Header = ({ closeDrawer }: HeaderProps) => {
  const editSubscription = useAtomValue(editSubscriptionAtom);
  const [createSubscription, setCreateSubscription] = useAtom(createSubscriptionAtom);
  const isEditCreateFormOpen = useAtomValue(isEditCreateFormOpenAtom);
  const resetAtoms = useResetPanelAtoms();
  const isAdminUser = useSelector(isAdminRoleSelector);
  const breadcrumbItems = [
    {
      key: 'Email Subscriptions',
      title: (
        <>
          Email Subscriptions
          <AdminOnly style={{ position: 'relative', top: '-3px', marginLeft: '10px' }} />
        </>
      ),
      clickable: isEditCreateFormOpen,
      show: true,
      onClick: isEditCreateFormOpen
        ? () => {
            resetAtoms.reset();
          }
        : undefined,
    },
    {
      key: 'New Subscription',
      title: 'New Subscription',
      clickable: false,
      show: createSubscription,
    },
    {
      key: 'Edit Subscription',
      title: 'Edit Subscription',
      clickable: false,
      show: !!editSubscription.emailId,
    },
  ];

  const emailsToUnsubscribe = useAtomValue(emailsToUnsubscribeAtom);
  const { mutate: unsubscribeEmails } = useUnsubscribeEmail();

  const handleUnsubscribe = async () => {
    await unsubscribeEmails(emailsToUnsubscribe || []);
    apptracker(UNSUBSCRIBE_EMAIL_SUBSCRIPTIONS, { emailIds: emailsToUnsubscribe || [] });
  };

  const handleCreate = () => {
    setCreateSubscription(true);
  };

  return (
    <HeaderWrapper>
      <LeftSideHeader>
        <Title>{DataSourceMapping[CURRENT_PRODUCT_MODE.toLowerCase()]}</Title>
        <Divider />

        <Breadcrumb separator=">">
          {breadcrumbItems.map((item) =>
            item.show ? (
              <Breadcrumb.Item onClick={item.onClick} key={item.key}>
                <Title clickable={item.clickable}>{item.title}</Title>
              </Breadcrumb.Item>
            ) : null,
          )}
        </Breadcrumb>
      </LeftSideHeader>

      <RightSideHeader>
        <HelpRedirect
          style={{ position: 'relative', top: '10px', right: '15px' }}
          redirectUrl={AI_ALERT_EMAIL_SUBSCRIPTION_HELP}
        />
        {!!emailsToUnsubscribe.length && isAdminUser && !isEditCreateFormOpen ? (
          <Popconfirm
            key="delete"
            placement="bottom"
            title="Unsubscribe Confirmation"
            description="Do you want to unsubscribe the selected emails from alert notifications?"
            icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
            okText="Unsubscribe"
            cancelText="Cancel"
            onConfirm={handleUnsubscribe}
          >
            <StyledButton type="primary" ghost icon={<DeleteOutlined />}>
              Unsubscribe
            </StyledButton>
          </Popconfirm>
        ) : null}
        {isAdminUser && !isEditCreateFormOpen ? (
          <StyledButton type="primary" ghost onClick={handleCreate} icon={<PlusOutlined />}>
            New Subscription
          </StyledButton>
        ) : null}
        <CloseOutlined style={{ paddingTop: '8px' }} onClick={closeDrawer} />
      </RightSideHeader>
    </HeaderWrapper>
  );
};
